<template>
  <div>
    <MessageBarV02
      color="#223448"
      class="message-bar"
      dark
      dense
      dismissible
      transition="fade-transition"
      tile
      v-model="showMessageBar"
    >
      {{ nonStatSigMessage }}
    </MessageBarV02>
    <BenchmarksHeader
      :initialDateValue="dateValue"
      :comparisonDateRange="comparisonDateRange"
      :comparisonPredefinedRangeValue="comparisonPredefinedRangeValue"
      :comparisonQuarter="predefinedComparisonQuarterValue"
      :minDate="minDate"
      :maxDate="maxDate"
      :displayLimit="0"
      :view="view"
      @add="showSlideOut = true"
      @complete="getCurrentActiveViewData"
      @updateInitialDateRange="updateInitialDateRange"
      @applyInitialDateRange="applyInitialDateRange"
      @applyComparisonQuarter="applyComparisonQuarter"
      @applyComparisonCustomDateRange="applyComparisonCustomDateRange"
    ></BenchmarksHeader>
    <BenchmarksSlideOut
      :show="showSlideOut"
      @cancel="showSlideOut = false"
      @complete="closeSlideOutAndUpdateOverview"
    />
    <div v-if="loading" class="benchmarks-loader">
      <Loader
        :size="90"
        :width="9"
      />
    </div>
    <div v-else>
      <div v-if="showExpandedView">
        <div class="actions-container" id="actions-container">
          <Breadcrumbs
            :breadcrumbsList="breadcrumbsList"
            @click="handleBreadcrumbClick"
          ></Breadcrumbs>
          <PrimaryButton @click="downloadData" :disabled="activeDimensionError">
            <template slot="icon">
              <ExportIcon :size="'small'"></ExportIcon>
            </template>
            Export as CSV
          </PrimaryButton>
        </div>
        <div class="content-container">
          <BenchmarksCardContainer
            v-bind="sharedCardProps"
            type="expanded"
            :data="getBenchmarksData(currentActiveDimension.value)"
            :dimension="currentActiveDimension.key"
            :title="currentActiveDimension.title"
            :summary="getDimensionSummary(currentActiveDimension.value)"
            :shouldBlurValues="shouldBlurValues(currentActiveDimension.value)"
            :error="isEmptyOrInvalidData(currentActiveDimension.value)"
            :sortKey="getCurrentSortKey(currentActiveDimension.value)"
            :sortOrder="getCurrentSortOrder(currentActiveDimension.value)"
            @sortData="sortChartData(currentActiveDimension.value, $event)"
          />
        </div>
      </div>
      <div v-else>
        <div
          class="actions-container"
          id="actions-container"
        >
          <div v-if="!hasCategoryError">
            <BenchmarksDescription
              :view="view"
              :category="formattedCategory"
            />
          </div>
          <PrimaryButton @click="downloadAllData" :disabled="allDimensionError">
            <template slot="icon">
              <ExportIcon :size="'small'"></ExportIcon>
            </template>
            Export All
          </PrimaryButton>
        </div>
        <div class="content-container">
          <div v-if="hasBrandError">
            <BenchmarksMessageContainer
              @add="handleAdd"
              :brandError="hasBrandError"
            />
          </div>
          <div v-else-if="hasCategoryError">
            <BenchmarksMessageContainer
              @add="handleAdd"
              :isOptOutFromCategoryBenchmarks="!!brandIsOptOutFromCategoryBenchmarks"
              :noCategorySelected="!selectedCategory"
            />
          </div>
          <BenchmarksCardContainer
            v-else
            v-for="(card, index) in localBenchmarksCards"
            :key="index"
            v-bind="sharedCardProps"
            type="summary"
            :data="getBenchmarksSummaryData(card.value)"
            :title="card.title"
            :dimension="card.key"
            :shouldBlurValues="shouldBlurValues(card.value)"
            :error="isEmptyOrInvalidData(card.value)"
            :sortKey="getCurrentSortKey(card.value)"
            :sortOrder="getCurrentSortOrder(card.value)"
            @expand="expandCard(card)"
            @sortData="sortChartData(card.value, $event)"
          />
        </div>
      </div>
    </div>
    <OnboardingTour
      v-if="!loading && !allDimensionError && !hasBrandError"
      tourName="Benchmarks"
      :tourSteps="steps"
      :beforeRestartHook="beforeTourRestartHook"
    ></OnboardingTour>
  </div>
</template>
<script>
import { isEqual } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import JSZip from 'jszip';

import {
  downloadFile,
  createBenchmarksFile,
  createAndDownloadBenchmarksFile,
} from '../../../services/dashboard/downloadService';
import { getLastQuarter } from '../../../services/dashboard/dates/datesService';
import {
  getBrandBenchmarks,
  getCategoryBenchmarks,
} from '../../../services/dashboard/benchmarksService';
import {
  getSelectedFilters,
  removeBrandOnlyFilters,
  benchmarksSpecialValues,
} from '../../../services/dashboard/filtersService';
import {
  getFilterSelectionsTotalCount,
  getErrorMessage,
} from '../../../services/dashboard/dashboardUtils';

import BenchmarksHeader from '../Benchmarks/BenchmarksHeader.vue';
import BenchmarksSlideOut from '../Common/SlideOut.vue';
import BenchmarksMessageContainer
  from '../Benchmarks/BenchmarksMessage/BenchmarksMessageContainer.vue';
import BenchmarksCardContainer
  from '../Benchmarks/BenchmarksCard/BenchmarksCardContainer.vue';
import BenchmarksDescription from '../Benchmarks/BenchmarksDescription.vue';
import {
  Breadcrumbs,
  ExportIcon,
  MessageBarV02,
  PrimaryButton,
} from '@mikmak/components';
import Loader from '../Common/Loader';
import OnboardingTour from './OnboardingTour';

import eventDispatcher from '../../../events/dispatch';
import { benchmarksTourSteps } from './config/onboardingTourSteps';
import {
  BENCHMARKS_TABS,
  BENCHMARKS_CHART_LABELS_STRUCTURE,
  benchmarksCards,
  benchmarksTitles,
  benchmarksValues,
} from './config/benchmarksCards';
import {
  rateDisplayCsvGetter,
} from '../../../services/dashboard/displayFormatService';

export default {
  props: {
    minDate: {
      type: Date,
    },
    maxDate: {
      type: Date,
    },
    dateRange: {
      type: Object,
    },
    dateValue: {
      type: Object,
    },
    startDate: {
      type: Date,
    },
    endDate: {
      type: Date,
    },
    startDateStr: {
      type: String,
    },
    endDateStr: {
      type: String,
    },
  },
  components: {
    BenchmarksHeader,
    BenchmarksSlideOut,
    BenchmarksCardContainer,
    PrimaryButton,
    ExportIcon,
    Breadcrumbs,
    BenchmarksMessageContainer,
    OnboardingTour,
    Loader,
    BenchmarksDescription,
    MessageBarV02,
  },
  data() {
    return {
      viewName: 'Benchmarks',
      loading: false,
      showSlideOut: false,
      showExpandedView: false,
      showMessageBar: false,
      currentActiveDimension: {},
      summaryDisplayLimit: 5,
      dashboardError: false,
      localBenchmarksCards: cloneDeep(benchmarksCards),
      steps: benchmarksTourSteps,
      nonStatSigMessage: 'There is non-significant data in your Category Benchmarks. Non-significant data does not meet Category Benchmarks thresholds and will not appear in your chart.',
    };
  },
  computed: {
    ...mapState({
      showNewBrandSelector: state => state.main.showNewBrandSelector,
      currentBrand: state => state.attachStudio.currentAttachBrand,
      portfolioBrands: state => state.dashboard.portfolioBrands,
      storedComparisonRange: state => state.dashboard.reportRequest.comparisonRange,
      activeBenchmarksView: state => state.benchmarks.activeBenchmarksView,
      benchmarksBrandData: state => state.benchmarks.brandData,
      benchmarksCategoryData: state => state.benchmarks.categoryData,
      dashboardSelectedFilters: state => state.filters.dashboardSelections.filters || [],
      allowedFilters: state => state.filters.allowedFilters.benchmarks,
      selectedCategory: state => state.dashboard.selectedCategory,
      selectedSubcategories: state => state.dashboard.selectedSubcategories,
      categoriesAndSubcategories: state => state.dashboard.categoriesAndSubcategories || {},
    }),
    ...mapGetters([
      'selectedBrandIds',
      'uniqueBenchmarkCategoryKeys',
    ]),
    filters() {
      return getSelectedFilters(
        this.dashboardSelectedFilters,
        this.allowedFilters,
      );
    },
    specialFiltersExcluded() {
      return this.filters
        .filter(f => !benchmarksSpecialValues().has(f.key));
    },
    sharedCardProps() {
      return {
        theme: this.view,
        loading: this.loading,
        labels: this.labels,
      };
    },
    hasBrandError() {
      return this.portfolioBrands.filter(pb => pb.checked).length !== 1;
    },
    hasCategoryError() {
      if (this.view !== 'category') return false;

      return this.brandIsOptOutFromCategoryBenchmarks || !this.selectedCategory;
    },
    brandIsOptOutFromCategoryBenchmarks() {
      return this.currentBrand && this.currentBrand.isOptOutFromCategoryBenchmarks;
    },
    activeDimensionError() {
      if (this.loading) return true;

      const dimensionValue = this.currentActiveDimension.value;

      return this.isEmptyOrInvalidData(dimensionValue) || this.shouldBlurValues(dimensionValue);
    },
    allDimensionError() {
      if (this.hasBrandError || this.loading) return true;

      return !this.currentActiveViewData || Object.keys(this.currentActiveViewData).every(d => {
        return d === 'uiData' || this.isEmptyOrInvalidData(d) || this.shouldBlurValues(d);
      });
    },
    categoryOnlyFilters() {
      return removeBrandOnlyFilters(this.specialFiltersExcluded);
    },
    currentActiveViewFilters() {
      return this.view === 'brand' ? this.specialFiltersExcluded : this.categoryOnlyFilters;
    },
    currentActiveViewData() {
      return this.view === 'brand' ? this.benchmarksBrandData : this.benchmarksCategoryData;
    },
    singleSelectBrandId() {
      return this.hasBrandError ? '' : this.currentBrand.brandId;
    },
    singleSelectBrandName() {
      return this.hasBrandError ? '' : this.currentBrand.brandName;
    },
    comparisonDateRange() {
      return {
        start: this.storedComparisonRange.start,
        end: this.storedComparisonRange.end,
      };
    },
    comparisonPredefinedRangeValue() {
      return this.storedComparisonRange.predefinedRangeValue || 0;
    },
    comparisonStartStr() {
      return this.formatDate(this.comparisonDateRange.start);
    },
    comparisonEndStr() {
      return this.formatDate(this.comparisonDateRange.end);
    },
    predefinedComparisonQuarterValue() {
      return this.storedComparisonRange.predefinedQuarterValue;
    },
    selectedPortfolioBrands() {
      return this.portfolioBrands.filter(pb => pb.checked);
    },
    invalidBrandSelection() {
      return this.selectedPortfolioBrands.length !== 1;
    },
    formattedCategory() {
      return this.getCategoryOrSubcategoryName(this.selectedCategory);
    },
    formattedInitialRange() {
      const displayStart = this.formatDisplayDate(this.dateRange.start);
      const displayEnd = this.formatDisplayDate(this.dateRange.end);

      return `${displayStart}-${displayEnd}`;
    },
    formattedComparisonRange() {
      const displayStart = this.formatDisplayDate(this.storedComparisonRange.start);
      const displayEnd = this.formatDisplayDate(this.storedComparisonRange.end);

      return `${displayStart}-${displayEnd}`;
    },
    primaryLabel() {
      return this.view === 'brand' ? this.formattedInitialRange : this.currentBrand.brandName;
    },
    secondaryLabel() {
      return this.view === 'brand' ? this.formattedComparisonRange : this.formattedCategory;
    },
    tertiaryLabel() {
      return this.getCategoryOrSubcategoryName(this.selectedSubcategories[0]);
    },
    quaternaryLabel() {
      return this.getCategoryOrSubcategoryName(this.selectedSubcategories[1]);
    },
    quinaryLabel() {
      return this.getCategoryOrSubcategoryName(this.selectedSubcategories[2]);
    },
    labels() {
      const labels = BENCHMARKS_CHART_LABELS_STRUCTURE[this.activeBenchmarksView];

      return labels.reduce((acc, label) => {
        const labelName = this[`${label}Label`];

        if (labelName) {
          acc[label] = labelName;
        }

        return acc;
      }, {});
    },
    view() {
      const tabObj = Object.entries(BENCHMARKS_TABS)
        .find(([_, value]) => value.routeName === this.$route.name);

      return (tabObj && tabObj[0]) || 'category';
    },
    reportViewName() {
      return this.view === 'category' ? 'Category' : 'Brand';
    },
    breadcrumbsList() {
      const formattedViewName = this.view.slice(0, 1).toUpperCase() + this.view.slice(1);

      return [
        { name: `All ${formattedViewName} Benchmarks` },
        {
          name: this.currentActiveDimension.title,
          dropdownNames: benchmarksTitles,
        },
      ];
    },
    sortingKey() {
      return this.view === 'brand' ? 'brandSorting' : 'categorySorting';
    },
    isNewState() {
      return !this.currentActiveViewData
        || Object.keys(this.currentActiveViewData.uiData)
          .some(key => this.currentActiveViewData.uiData[key] !== this[key]);
    },
    benchmarksAnalyticsObj() {
      const filters = this.currentActiveViewFilters;
      const filterDimensions = filters.map(f => f.key);

      const filterSelectionsTotalCount = getFilterSelectionsTotalCount(filters);

      return {
        dashboard_view: this.viewName,
        benchmarks_view: this.view,
        dimension_view: this.currentActiveDimension?.value,
        brand_id: this.singleSelectBrandId,
        brand_name: this.singleSelectBrandName,
        start_date: this.startDateStr,
        end_date: this.endDateStr,
        comparison_start: this.comparisonStartStr,
        comparison_end: this.comparisonEndStr,
        filters: this.stringifiedFilters,
        filter_count: filters.length,
        filter_selections_total_count: filterSelectionsTotalCount,
        filter_stringified_length: this.stringifiedFilters.length,
        filter_dimensions: filterDimensions.join(', '),
        filter_dimensions_count: filterDimensions.length,
        selectedCategory: this.selectedCategory,
        selectedSubcategories: this.selectedSubcategories,
      };
    },
    stringifiedFilters() {
      return JSON.stringify(this.currentActiveViewFilters);
    },
    hasNonStatSigCategoryData() {
      if (this.view !== 'category' || !this.benchmarksCategoryData) return;

      const dimensionReports = Object.values(this.benchmarksCategoryData);

      return dimensionReports.some(({ benchmarks = [] }) => {
        return benchmarks.some(benchmarkObj => Object.values(benchmarkObj).includes(null));
      });
    },
    showNonStatSigMessage() {
      return this.view === 'category'
        && !this.loading
        && this.hasNonStatSigCategoryData;
    },
  },
  methods: {
    getCategoryOrSubcategoryName(id) {
      const categoryOrSubcategory = this.categoriesAndSubcategories[id];

      return categoryOrSubcategory?.key?.replaceAll('_', ' ') || '';
    },
    async closeSlideOutAndUpdateOverview() {
      this.showSlideOut = false;
      await this.getCurrentActiveViewData();
    },
    stringifyFilterValue(filter) {
      return filter.join(', ');
    },
    handleRouteChange(tab) {
      const tabObj =
        Object.entries(BENCHMARKS_TABS).find(([_, value]) => value.name === tab);
      const view = tabObj ? tabObj[0] : 'category';

      this.$store.commit('updateActiveBenchmarksView', view);
      this.updateViewRoute();

      if (this.storedComparisonRange.predefinedQuarterValue === null) this.resetComparisonRange();

      this.getCurrentActiveViewData();
    },
    handleAdd() {
      if (this.showNewBrandSelector) {
        const brandSelectorEl = document.querySelector('.brand-selector__select .v-input__slot');

        if (brandSelectorEl) brandSelectorEl.click();
      } else {
        this.showSlideOut = true;
      }
    },
    updateViewRoute() {
      const currentRouteName = BENCHMARKS_TABS[this.view].routeName;

      if (this.$route.name !== currentRouteName) {
        this.$router.push({
          name: currentRouteName,
        });

        eventDispatcher('dashboardBenchmarksViewSelected', {
          ...this.benchmarksAnalyticsObj,
          has_brand_error: this.hasBrandError,
        });
      }
    },
    async fetchBrandDataByDimension(dimensions) {

      const payload = {
        brandId: this.singleSelectBrandId,
        initialStart: this.startDateStr,
        initialEnd: this.endDateStr,
        comparisonStart: this.comparisonStartStr,
        comparisonEnd: this.comparisonEndStr,
        filters: this.currentActiveViewFilters,
        dimensions: dimensions,
        categoryId: this.selectedCategory,
        subcategoryIds: this.selectedSubcategories,
      };

      try {
        const token = await this.$auth.getTokenSilently();

        return await getBrandBenchmarks(payload, token);
      } catch (error) {
        this.dashboardError = true;
        this.$emit('showMessage', { type: 'dashboardError' });
      }
    },
    async fetchCategoryDataByDimension(dimensions) {

      const payload = {
        brandId: this.singleSelectBrandId,
        initialStart: this.startDateStr,
        initialEnd: this.endDateStr,
        comparisonStart: this.comparisonStartStr,
        comparisonEnd: this.comparisonEndStr,
        filters: this.currentActiveViewFilters,
        dimensions: dimensions,
        categoryId: this.selectedCategory,
        subcategoryIds: this.selectedSubcategories,
      };

      try {
        const token = await this.$auth.getTokenSilently();

        return await getCategoryBenchmarks(payload, token);
      } catch (error) {
        this.dashboardError = true;
        this.$emit('showMessage', { type: 'dashboardError' });
      }
    },
    async getBenchmarksBrandData() {
      this.loading = true;
      this.$store.commit('setIsLoading', true);
      this.dashboardError = false;
      const requestTime = Date.now();
      const benchmarksAnalyticsObj = { ...this.benchmarksAnalyticsObj };
      let errorMessage = '';

      eventDispatcher('dashboardRequest', benchmarksAnalyticsObj);

      try {
        const brandData =
          await this.fetchBrandDataByDimension(benchmarksValues);

        if (brandData) {
          brandData.uiData = {
            singleSelectBrandId: this.singleSelectBrandId,
            startDateStr: this.startDateStr,
            endDateStr: this.endDateStr,
            stringifiedFilters: this.stringifiedFilters,
          };
        }

        this.$store.commit('updateBenchmarksBrandData', brandData);
      } catch (error) {
        this.$store.commit('updateBenchmarksBrandData', undefined);
        this.dashboardError = true;
        errorMessage = getErrorMessage(error);
        this.$emit('showMessage', { type: 'dashboardError' });
      } finally {
        const total_time_ms = Date.now() - requestTime;

        eventDispatcher('dashboardRequestResponse', {
          total_time_ms,
          has_error: this.dashboardError,
          error_message: errorMessage,
          ...benchmarksAnalyticsObj,
        });

        this.loading = false;
        this.$store.commit('setIsLoading', false);
      }
    },
    async getBenchmarksCategoryData() {
      this.loading = true;
      this.$store.commit('setIsLoading', true);
      this.dashboardError = false;
      const requestTime = Date.now();
      const benchmarksAnalyticsObj = { ...this.benchmarksAnalyticsObj };

      eventDispatcher('dashboardRequest', benchmarksAnalyticsObj);

      try {
        if (this.view === 'category'
          && (this.brandIsOptOutFromCategoryBenchmarks || !this.selectedCategory)
        ) {
          return;
        }

        const categoryData =
          await this.fetchCategoryDataByDimension(benchmarksValues);

        if (categoryData) {
          categoryData.uiData = {
            singleSelectBrandId: this.singleSelectBrandId,
            startDateStr: this.startDateStr,
            endDateStr: this.endDateStr,
            stringifiedFilters: this.stringifiedFilters,
          };
        }

        if (this.isNewState) {
          for (const dimension of this.localBenchmarksCards) {
            this.updateSortKey(dimension.value, categoryData[dimension.value]['benchmarks']);
          }
        }

        this.$store.commit('updateBenchmarksCategoryData', categoryData);
      } catch (error) {
        this.$store.commit('updateBenchmarksCategoryData', undefined);
        this.dashboardError = true;
        this.$emit('showMessage', { type: 'dashboardError' });
      } finally {
        const total_time_ms = Date.now() - requestTime;

        eventDispatcher('dashboardRequestResponse', {
          total_time_ms,
          has_error: this.dashboardError,
          ...benchmarksAnalyticsObj,
        });

        this.loading = false;
        this.$store.commit('setIsLoading', false);
      }
    },
    async getCurrentActiveViewData() {
      if (!this.singleSelectBrandId) return;

      this.showMessageBar = false;
      this.view === 'brand'
        ? await this.getBenchmarksBrandData()
        : await this.getBenchmarksCategoryData();
    },
    getCurrentSortKey(dimension) {
      const card = this.localBenchmarksCards.find(card => card.value === dimension);

      return card[this.sortingKey].sortKey;
    },
    getCurrentSortOrder(dimension) {
      const card = this.localBenchmarksCards.find(card => card.value === dimension);

      return card[this.sortingKey].sortOrder;
    },
    formatDate(date) {
      return format(date, 'yyyy-MM-dd');
    },
    formatDisplayDate(date) {
      return format(date, 'MMM dd').toUpperCase();
    },
    updateInitialDateRange(value) {
      const { predefinedRangeValue, range } = value;

      this.$store.commit('updatePredefinedRangeValue', predefinedRangeValue);

      this.$store.commit('updateReportEndDate', range.end);
      this.$store.commit('updateReportStartDate', range.start);
    },
    applyInitialDateRange(value) {
      this.updateInitialDateRange(value);

      this.$nextTick(() => {
        this.getCurrentActiveViewData();
      });
    },
    applyComparisonQuarter(selectedQuarter) {

      const comparisonDateRange = {
        start: selectedQuarter.range.start,
        end: selectedQuarter.range.end,
        predefinedQuarterValue: selectedQuarter.value,
      };

      this.$store.commit('updateReportComparisonRange', comparisonDateRange);

      this.$nextTick(() => {
        this.getCurrentActiveViewData();

        eventDispatcher('dashboardBenchmarksQuarterSelected', {
          ...this.benchmarksAnalyticsObj,
          selected_quarter: selectedQuarter.key,
          expanded_view: this.showExpandedView,
        });
      });
    },
    applyComparisonCustomDateRange(val) {

      const { predefinedRangeValue, range } = val;

      const comparisonDateRange = {
        start: range.start,
        end: range.end,
        predefinedRangeValue: predefinedRangeValue,
        predefinedQuarterValue: null,
      };

      this.$store.commit('updateReportComparisonRange', comparisonDateRange);

      this.$nextTick(() => {
        this.getCurrentActiveViewData();
      });
    },
    resetComparisonRange() {
      const comparisonDateRange = getLastQuarter();

      this.$store.commit('updateReportComparisonRange', comparisonDateRange);
    },
    expandCard(dimension) {
      this.showExpandedView = true;
      this.currentActiveDimension = dimension;

      eventDispatcher('dashboardBenchmarksDimensionExpanded', {
        ...this.benchmarksAnalyticsObj,
      });
    },
    handleBreadcrumbClick(val) {
      if (val.slice(0, 3) === 'All') {
        this.showExpandedView = false;
      } else {
        this.currentActiveDimension = this.localBenchmarksCards.find(c => c.title === val);
      }
    },
    isInvalidData(dimension) {
      return !this.currentActiveViewData || !this.currentActiveViewData[dimension];
    },
    isEmptyOrInvalidData(dimension) {
      return this.isInvalidData(dimension)
        || this.currentActiveViewData[dimension]['benchmarks'].length === 0;
    },
    shouldBlurValues(dimension) {
      return this.isInvalidData(dimension)
        ? false
        : !this.currentActiveViewData[dimension]['isStatisticallySignificant'] && !this.loading;
    },
    updateSortKey(dimension, data) {
      const allCategoryValuesNull = data.every(el => el.category === null);

      const card = this.localBenchmarksCards.find(card => card.value === dimension);

      card.categorySorting.sortKey = allCategoryValuesNull ? 'base' : 'category';
    },
    getBenchmarksData(dimension) {
      if (this.isInvalidData(dimension)) return [];

      const data = [...this.currentActiveViewData[dimension]['benchmarks']];

      const sortKey = this.getCurrentSortKey(dimension);
      const sortOrder = this.getCurrentSortOrder(dimension);

      return data.sort((a, b) => {
        a = a[sortKey];
        b = b[sortKey];
        if (a === b) return 0;
        else if (a === null) return 1;
        else if (b === null) return -1;
        else
          return (a > b ? -1 : 1) * sortOrder;
      });
    },
    getBenchmarksSummaryData(dimension) {
      return this.getBenchmarksData(dimension).slice(0, this.summaryDisplayLimit);
    },
    getDimensionSummary(dimension) {
      return this.isInvalidData(dimension)
        ? []
        : this.currentActiveViewData[dimension]['summary'];
    },
    downloadData() {
      const dimension = this.currentActiveDimension;
      const columns = this.downloadedReportColumns(dimension.key);
      const rowData =
        cloneDeep(this.currentActiveViewData[this.currentActiveDimension.value]['benchmarks']);

      let fileName = '';
      let fileSize = '';
      let downloadError = false;

      try {
        const reportName = this.getDownloadFileName(dimension);
        const downloadObject = createAndDownloadBenchmarksFile(columns, rowData, reportName);

        fileName = downloadObject.name;
        fileSize = downloadObject.size;
      } catch (err) {
        this.$emit('showMessage', { type: 'csvDownloadError' });

        downloadError = true;
      } finally {
        eventDispatcher('dashboardCSVDownloaded', {
          file_name: fileName,
          file_size: fileSize,
          ...this.benchmarksAnalyticsObj,
          has_error: downloadError,
        });
      }
    },
    downloadAllData() {
      const zip = new JSZip();
      const jsonObj = cloneDeep(this.currentActiveViewData);

      for (const dimension of this.localBenchmarksCards) {
        const columns = this.downloadedReportColumns(dimension.key);
        const rowData = jsonObj[dimension.value]['benchmarks'];

        if (rowData.length === 0) continue;

        const individualFileName = this.getDownloadFileName(dimension);

        const fileObject = createBenchmarksFile(columns, rowData, individualFileName);

        zip.file(fileObject.name, fileObject.csv);
      }

      zip.generateAsync({ type: 'blob' })
        .then((content) => {
          const fileSize = content.size;
          const fileName = `MikMakBenchmarksInsights${this.reportViewName}.zip`;
          let downloadError = false;

          try {
            downloadFile(content, fileName);
          } catch (err) {
            this.$emit('showMessage', { type: 'csvDownloadError' });

            downloadError = true;
          } finally {
            eventDispatcher('dashboardZIPDownloaded', {
              file_name: fileName,
              file_size: fileSize,
              ...this.benchmarksAnalyticsObj,
              has_error: downloadError,
            });
          }
        });
    },
    getDownloadFileName(dimension) {
      return `${this.reportViewName}${dimension.reportTitle}`;
    },
    downloadedReportColumns(dimensionName) {
      const secondaryValue = this.view === 'category' ? 'category' : 'comparison';
      const reportColumns = [
        { label: dimensionName, value: 'dimension' },
        { label: this.primaryLabel, value: rateDisplayCsvGetter('base') },
        { label: this.secondaryLabel, value: rateDisplayCsvGetter(secondaryValue) },
        { label: 'Diff', value: 'diff' },
      ];

      if (this.uniqueBenchmarkCategoryKeys?.has('subcategoryOne')) {
        reportColumns.push(
          {
            label: this.tertiaryLabel,
            value: rateDisplayCsvGetter('subcategoryOne'),
          });
      }

      if (this.uniqueBenchmarkCategoryKeys?.has('subcategoryTwo')) {
        reportColumns.push(
          {
            label: this.quaternaryLabel,
            value: rateDisplayCsvGetter('subcategoryTwo'),
          });
      }

      if (this.uniqueBenchmarkCategoryKeys?.has('subcategoryThree')) {
        reportColumns.push(
          {
            label: this.quinaryLabel,
            value: rateDisplayCsvGetter('subcategoryThree'),
          });
      }

      return reportColumns;
    },
    sortChartData(dimension, sorting) {
      const card = this.localBenchmarksCards.find(card => card.value === dimension);

      card[this.sortingKey].sortKey = sorting.key;
      card[this.sortingKey].sortOrder = sorting.order;
    },
    beforeTourRestartHook() {
      if (this.showExpandedView) {
        document.querySelector('.breadcrumbs-container .page-left').click();
      }
    },
  },
  watch: {
    $route(newRoute = {}) {
      const benchmarksTabs = Object.values(BENCHMARKS_TABS);
      const { name } = benchmarksTabs.find(tab => tab.routeName === newRoute.name) || {};

      this.showSlideOut = false;
      this.handleRouteChange(name);
    },
    async selectedBrandIds(newIds, oldIds) {
      if (!this.showNewBrandSelector) return;

      if (this.isNewState && !isEqual(newIds, oldIds)) {
        this.$emit('clearSelectedFilters');
        await this.getCurrentActiveViewData();
      }
    },
    benchmarksCategoryData() {
      this.showMessageBar = this.showNonStatSigMessage;
    },
  },
  beforeMount() {
    this.$store.commit('updateActiveView', this.viewName);
  },
  mounted() {
    this.$store.commit('updateActiveBenchmarksView', this.view);
    this.$store.commit('updateBenchmarksCategory', this.currentBrand.categoryId);
    this.$store.commit('updateBenchmarksSubcategories', this.currentBrand.subCategoryIds);

    if (!this.hasBrandError && this.isNewState) {
      this.getCurrentActiveViewData();
    }
  },
};
</script>
<style lang="scss" scoped>
.actions-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 24px 0;

  & ::v-deep {
    & ul.options {
      overflow-y: auto;
    }

    & .main-button {
      padding: 14px 16px;
    }

    & .main-button-wrapper {
      min-width: 130px;
    }
  }
}

.content-container {
  padding-bottom: 200px;
}

.benchmarks-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 120px;
}

.message-bar {
  width: calc(100% + 48px);
  margin-left: -24px;
}
</style>
